import { Node } from 'reactflow';
import useStore from '@components/MainStage/store';

const MISSING_ELEMENT_RETRY_MS = 50;

/**
 * DOM and store access for one layer or sub-layer of the canvas, addressed by
 * the id its React Flow node and its DOM element share.
 *
 * Resolves both lazily instead of holding a MutationObserver and a store
 * subscription: a redraw touches every layer, so ~26 instances observing the
 * whole document and scanning every node on every write cost more than the
 * lookups they replaced. The element reference is revalidated through
 * `isConnected` because React replaces the DOM node on remount, and a failed
 * lookup is retried at most every MISSING_ELEMENT_RETRY_MS so a layer that has
 * not rendered yet cannot be queried once per access.
 */
class LayerViewBase<IdType extends string> {
  protected readonly id: IdType;

  private cachedElement: HTMLElement | null = null;

  private lastMissedAt = 0;

  private cachedNode: Node | null = null;

  private cachedNodesSource: Node[] | null = null;

  constructor(id: IdType) {
    this.id = id;
  }

  get element(): HTMLElement | null {
    if (this.cachedElement?.isConnected) return this.cachedElement;

    const now = performance.now();

    if (
      this.cachedElement === null &&
      this.lastMissedAt &&
      now - this.lastMissedAt < MISSING_ELEMENT_RETRY_MS
    ) {
      return null;
    }

    this.cachedElement = document.getElementById(this.id);
    this.lastMissedAt = this.cachedElement ? 0 : now;

    return this.cachedElement;
  }

  get node(): Node | null {
    const { nodes } = useStore.getState();

    if (nodes !== this.cachedNodesSource) {
      this.cachedNodesSource = nodes;
      this.cachedNode = nodes.find((node) => node.id === this.id) ?? null;
    }

    return this.cachedNode;
  }

  get height(): number {
    return this.element?.clientHeight ?? 0;
  }

  dispose(): void {
    this.cachedElement = null;
    this.cachedNode = null;
    this.cachedNodesSource = null;
  }
}

export default LayerViewBase;
